import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Calendar, Target, AlertTriangle, ArrowLeft, Sparkles } from "lucide-react";

export default function AscensionPlan() {
  const { id } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    api.post(`/scan/${id}/plan`)
      .then((r) => setPlan(r.data))
      .catch((err) => toast.error(err?.response?.data?.detail || "Could not load plan"))
      .finally(() => setLoading(false));
  }, [id]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/scan/${id}/plan/refresh`);
      setPlan(data);
      toast.success("Plan regenerated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1200px] mx-auto px-6 py-20 text-center">
        <Loader2 className="w-10 h-10 animate-spin text-[var(--primary)] mx-auto mb-4" />
        <div className="font-display text-3xl text-[var(--primary)] glow">GENERATING YOUR PROTOCOL…</div>
        <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mt-2">
          AI is tailoring a 30-day plan to your exact scan
        </div>
      </div>
    );
  }

  if (!plan) return <div className="p-10 text-center font-mono text-sm">No plan available.</div>;

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10">
      <Link
        to={`/scan/${id}`}
        className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--primary)] inline-flex items-center gap-2 mb-6"
        data-testid="plan-back"
      >
        <ArrowLeft className="w-3 h-3" /> Back to scan
      </Link>

      <div className="grid grid-cols-12 gap-6 mb-10 items-end">
        <div className="col-span-12 md:col-span-8">
          <span className="tape-label">PERSONALIZED PROTOCOL</span>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-none" data-testid="plan-headline">
            {plan.headline}
          </h1>
          <p className="text-[var(--primary)] glow font-mono text-sm mt-4" data-testid="plan-gain">
            {plan.estimated_gain}
          </p>
        </div>
        <div className="col-span-12 md:col-span-4 text-right">
          <button onClick={refresh} disabled={refreshing} className="btn-ghost text-xs" data-testid="plan-refresh">
            <RefreshCw className={`inline w-3.5 h-3.5 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Regenerate
          </button>
        </div>
      </div>

      {/* Weeks */}
      <div className="space-y-px bg-[var(--border)] mb-10">
        {(plan.weeks || []).map((w) => (
          <div key={w.week} className="bg-[var(--surface)] p-6 grid grid-cols-12 gap-6" data-testid={`plan-week-${w.week}`}>
            <div className="col-span-12 md:col-span-3">
              <Calendar className="w-5 h-5 text-[var(--primary)] mb-2" />
              <div className="font-display text-5xl text-[var(--primary)] glow">W{w.week}</div>
              <div className="text-sm font-mono text-white mt-2">{w.focus}</div>
            </div>
            <div className="col-span-12 md:col-span-6">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-3">DAILY PROTOCOL</div>
              <ul className="space-y-2 text-sm">
                {(w.daily_protocol || []).map((d, i) => (
                  <li key={i} className="flex gap-3 border-l border-[var(--primary-dim)] pl-3">
                    <span className="text-[var(--primary)] font-mono text-xs">{String(i + 1).padStart(2, "0")}</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-12 md:col-span-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-3">CHECK</div>
              <ul className="space-y-2 text-xs font-mono">
                {(w.weekly_milestones || []).map((m, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Target className="w-3 h-3 text-[var(--primary)] mt-0.5 shrink-0" />
                    <span>{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      {/* Stack */}
      <div className="panel p-6 mb-10">
        <span className="tape-label">YOUR STACK</span>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)] mt-6">
          {(plan.stack || []).map((s) => (
            <div key={s.category} className="bg-[var(--surface-2)] p-4" data-testid={`stack-${s.category.toLowerCase()}`}>
              <div className="font-display text-2xl text-[var(--primary)] mb-3">{s.category.toUpperCase()}</div>
              <ul className="space-y-1.5 text-sm">
                {(s.items || []).map((item, i) => (
                  <li key={i} className="text-[var(--text)] flex gap-2">
                    <span className="text-[var(--primary)] font-mono">+</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Red flags + tracking */}
      <div className="grid grid-cols-12 gap-6 mb-10">
        <div className="col-span-12 md:col-span-6 panel p-6">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-[var(--danger)]" />
            <span className="tape-label tape-danger">WATCH-OUTS</span>
          </div>
          <ul className="space-y-2 text-sm">
            {(plan.red_flags || []).map((r, i) => (
              <li key={i} className="border-l border-[var(--danger)] pl-3 text-[var(--text)]">{r}</li>
            ))}
          </ul>
        </div>
        <div className="col-span-12 md:col-span-6 panel p-6">
          <div className="flex items-center gap-3 mb-4">
            <Target className="w-5 h-5 text-[var(--primary)]" />
            <span className="tape-label">TRACKING</span>
          </div>
          <ul className="space-y-2 text-sm font-mono">
            {(plan.tracking || []).map((t, i) => (
              <li key={i} className="border-l border-[var(--primary)] pl-3 text-[var(--text)]">{t}</li>
            ))}
          </ul>
        </div>
      </div>

      <Link to="/coach" className="btn-brutal inline-flex items-center gap-2" data-testid="plan-to-coach">
        <Sparkles className="w-4 h-4" /> Ask Coach about this plan →
      </Link>
    </div>
  );
}
